/**
 * The one message, seen from the two tabs that edit it.
 *
 * WHY THIS EXISTS
 * ---------------
 * The panel used to hold two bodies — a plain-text one and an HTML one — and ask which of them to
 * send. That is two e-mails to keep in step, and the author is the one who pays for the drift. So
 * there is now ONE source, the HTML, and the «Metin» tab is a second way into it rather than a
 * second body. These helpers are what makes that possible in the browser: take the document apart,
 * edit its body, put it back without losing the head, and know when the simple editor may be used.
 *
 * Nothing here cleans anything. Cleaning is the server's, through one call that the preview and the
 * send path share; what happens here is display and editing, and whatever it produces is sent to
 * that same server call to be checked.
 */

/** The tags a designed layout is built from — and the ones the simple editor must not flatten. */
const LAYOUT_TAGS = ['table', 'img', 'div', 'center', 'hr', 'blockquote'];

/**
 * May the «Metin» tab edit this, or would editing it cost the author their design?
 *
 * A message of paragraphs, headings, lists and links is one a person can write in a normal editor,
 * and that is what the product's own standard message is. A message built out of tables and images
 * is a LAYOUT: dropping it into a rich-text box would let one stray keystroke dissolve a design
 * somebody spent an afternoon on, and silently. So a layout is shown, not edited, and the HTML tab
 * says where to continue.
 */
export function isSimpleEmailHtml(html: string | null): boolean {
  if (!html || html.trim() === '') return true;
  const body = bodyFragment(html);
  return !LAYOUT_TAGS.some((tag) => new RegExp(`<${tag}\\b`, 'i').test(body));
}

/**
 * The body of a message the SERVER has cleaned, ready to be put into the editing host.
 *
 * A separate name from `bodyFragment` on purpose, and the only one the panel uses for that
 * injection: it says, at the call site, which document this is allowed to be. The caller passes
 * the source it holds as server-cleaned, and passing anything else is the mistake this name exists
 * to make visible.
 */
export function serverCleanedEmailBody(cleanedHtml: string | null): string {
  return cleanedHtml === null ? '' : bodyFragment(cleanedHtml);
}

/** The body of a document, or the fragment itself when there is no document around it. */
export function bodyFragment(html: string): string {
  const m = /<body\b[^>]*>([\s\S]*?)<\/body>/i.exec(html);
  return (m ? (m[1] ?? '') : html).trim();
}

/**
 * The same document with a new body, keeping everything around it.
 *
 * The head carries the `@media` rules the server kept, and the body tag carries the page's own
 * background and spacing. Rebuilding the document from the fragment alone would throw both away on
 * the first keystroke in the «Metin» tab — which is how an edit to one word used to be able to
 * undo a responsive design.
 */
export function withBodyFragment(html: string | null, fragment: string): string {
  if (!html || !/<body\b[^>]*>/i.test(html)) return fragment;
  return html.replace(/(<body\b[^>]*>)([\s\S]*?)(<\/body>)/i, (_whole, open: string, _old, close: string) =>
    [open, fragment, close].join(''),
  );
}

/** The placeholder every message's main link points at. Named once, used by both tabs. */
export const CERTIFICATE_LINK = '{{certificate_link}}';

/** Does this source really carry the main link as a link, rather than as visible text? */
export function hasCertificateLinkTarget(html: string | null): boolean {
  if (!html) return false;
  return /href\s*=\s*["']\s*\{\{\s*certificate_link\s*\}\}\s*["']/i.test(html);
}
